import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'

/**
 * usePriorities — fetch and mutate typed Priority rows for a session.
 *
 * Returns { priorities, loading, error, reload, addPriority,
 *           updatePriority, carryPriority }.
 *
 * Phase H: Priority typed rows + S11 carry-forward.
 *
 * Hard cap: 5 priorities per session (enforced by the server — the hook
 * surfaces a 409 error if the cap is reached).
 *
 * Carry mechanic:
 *  - carryPriority(priorityId, toSessionId?) calls the carryPriority mutation.
 *  - The server resolves (or creates) the next session atomically.
 *  - On success the old priority row is updated to status=carried; the new
 *    priority and carry edge are added to local state for immediate UI update
 *    (the new priority is in a different session so it is NOT added to this
 *    session's priorities list — only the old priority's status changes).
 *
 * Session is identified by sessionId. The `enabled` flag mirrors useInSession
 * and useRating — pass false to defer fetching (e.g. while session is loading).
 */

const LIST_PRIORITIES = `
  query ListPriorities($sessionId: ID!) {
    listPriorities(sessionId: $sessionId) {
      priorities {
        priorityId sessionId ownerId text rank status
        goalId dueBy carriedFromId createdAt updatedAt
      }
    }
  }
`

const ADD_PRIORITY = `
  mutation AddPriority($sessionId: ID!, $input: AddPriorityInput!) {
    addPriority(sessionId: $sessionId, input: $input) {
      priorityId sessionId ownerId text rank status
      goalId dueBy carriedFromId createdAt updatedAt
    }
  }
`

const UPDATE_PRIORITY = `
  mutation UpdatePriority($sessionId: ID!, $priorityId: ID!, $input: UpdatePriorityInput!) {
    updatePriority(sessionId: $sessionId, priorityId: $priorityId, input: $input) {
      priorityId sessionId ownerId text rank status
      goalId dueBy carriedFromId createdAt updatedAt
    }
  }
`

const CARRY_PRIORITY = `
  mutation CarryPriority($priorityId: ID!, $input: CarryPriorityInput) {
    carryPriority(priorityId: $priorityId, input: $input) {
      oldPriority {
        priorityId sessionId status updatedAt
      }
      newPriority {
        priorityId sessionId ownerId text rank status carriedFromId createdAt updatedAt
      }
      carryEdge {
        carryId sessionId fromId toId createdAt
      }
    }
  }
`

export function usePriorities(sessionId, { enabled = true } = {}) {
  const [priorities, setPriorities] = useState([])
  const [loading, setLoading] = useState(!!(sessionId && enabled))
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!sessionId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_PRIORITIES, { sessionId })
      setPriorities(data.listPriorities?.priorities ?? [])
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    if (enabled && sessionId) load()
  }, [load, enabled, sessionId])

  const addPriority = useCallback(
    async (input) => {
      const data = await appsyncQuery(ADD_PRIORITY, { sessionId, input })
      const p = data.addPriority
      setPriorities((prev) => [...prev, p].sort((a, b) => a.rank - b.rank))
      return p
    },
    [sessionId],
  )

  const updatePriority = useCallback(
    async (priorityId, input) => {
      const data = await appsyncQuery(UPDATE_PRIORITY, { sessionId, priorityId, input })
      const updated = data.updatePriority
      setPriorities((prev) =>
        prev
          .map((p) => (p.priorityId === priorityId ? { ...p, ...updated } : p))
          .sort((a, b) => a.rank - b.rank),
      )
      return updated
    },
    [sessionId],
  )

  const carryPriority = useCallback(
    async (priorityId, toSessionId) => {
      const input = toSessionId ? { toSessionId } : {}
      const data = await appsyncQuery(CARRY_PRIORITY, { priorityId, input })
      const result = data.carryPriority
      // Update the old priority's status to 'carried' in local state.
      // The new priority lives in a different session so we do not append it here.
      setPriorities((prev) =>
        prev.map((p) =>
          p.priorityId === priorityId ? { ...p, status: 'carried' } : p,
        ),
      )
      return result
    },
    [],
  )

  return {
    priorities,
    loading,
    error,
    reload: load,
    addPriority,
    updatePriority,
    carryPriority,
  }
}
